import {
    ApolloClient,
    ApolloProvider,
    from,
    HttpLink,
    InMemoryCache,
    NormalizedCacheObject,
    split,
    TypePolicies
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { useAuth0 } from "@auth0/auth0-react";
import getEnvVariables from "config/envVariables";
import { createClient } from "graphql-ws";
import "mapbox-gl/dist/mapbox-gl.css";
import { PropsWithChildren } from "react";

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;

const typePolicies: TypePolicies = {
    Query: {
        fields: {
            in_queue_clusters: {
                // When querying clusters by slug, use any cached clusters.
                // This is necessary because we're filtering the `in_queue_clusters`
                // type with a `where` clause, which returns an array of clusters,
                // even if there's only one match. Apollo doesn’t automatically
                // normalize such filtered queries.
                // An alternative approach would be to create a custom Hasura
                // query like `in_queue_clusters_by_slug`, which would return a
                // single record and be automatically normalized.
                read(existing, { args, toReference }) {
                    const where = args?.where;

                    // Only use the cached value for queries fetching *exactly*
                    // one cluster by slug.
                    if (
                        where &&
                        Object.keys(where).length === 1 &&
                        where.slug?._eq !== undefined
                    ) {
                        const slug = where.slug._eq;
                        const ref = toReference({
                            __typename: "in_queue_clusters",
                            slug
                        });
                        return ref ? [ref] : [];
                    }

                    return existing;
                }
            }
        }
    },
    in_queue_clusters: {
        // Cache clusters by slug rather than ID to use cached records when
        // loading cluster pages.
        keyFields: ["slug"]
    },
    advanced_studies: {
        keyFields: ["advanced_study_id"]
    },
    scenarios: {
        keyFields: ["scenario_id"]
    },
    scenario_projects: {
        keyFields: ["scenario_id", "project_id"]
    },
    // Scenario results arrive in bulk: one query returns every row for a
    // scenario, and the rows are copied straight into React state. Nothing
    // reads or writes an individual row through the cache, so normalizing
    // them buys identity no one uses and costs a cache entity plus a
    // dependency record per field per row. Anything that starts addressing a
    // single row wants the table's Hasura primary key here instead.
    scenario_allocated_upgrades: {
        keyFields: false
    },
    scenario_constraints: {
        keyFields: false
    },
    users: {
        keyFields: ["auth0_id"]
    },
    custom_upgrade_model_set_validations: {
        keyFields: ["id"]
    },
    custom_upgrade_model_set_validation_failures: {
        keyFields: ["id"]
    },
    // The table has no `id`, so tell Apollo how to normalize its rows.
    project_configurations: {
        keyFields: ["team_id", "cluster_id", "project_id"]
    }
};

const createApolloClient = (getToken: () => Promise<string | undefined>) => {
    if (apolloClient) {
        return apolloClient;
    }

    const envVariables = getEnvVariables();
    const isLocalhost = envVariables.hasuraUrl.startsWith("localhost");
    const hasuraHTTPScheme = isLocalhost ? "http" : "https";
    const hasuraWSScheme = isLocalhost ? "ws" : "wss";

    // Get auth token at request time.
    const authLink = setContext(
        async (_, { headers }: { headers: Record<string, string> }) => {
            const token = await getToken();
            return {
                headers: {
                    ...headers,
                    authorization: token ? `Bearer ${token}` : ""
                }
            };
        }
    );

    const httpLink = new HttpLink({
        uri: `${hasuraHTTPScheme}://${envVariables.hasuraUrl}`
    });

    const wsLink = new GraphQLWsLink(
        createClient({
            url: `${hasuraWSScheme}://${envVariables.hasuraUrl}`,
            connectionParams: async () => {
                const token = await getToken();
                return {
                    headers: {
                        authorization: token ? `Bearer ${token}` : ""
                    }
                };
            }
        })
    );

    const splitLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return (
                definition.kind === "OperationDefinition" &&
                definition.operation === "subscription"
            );
        },
        wsLink,
        from([authLink, httpLink])
    );

    apolloClient = new ApolloClient({
        link: splitLink,
        cache: new InMemoryCache({
            typePolicies,
            // Apollo memoizes reads with one entry per object per query, and
            // optimism caps that at 2^16 by default. Compare-scenarios reads
            // ~98k rows, so at the default the whole-query entry is evicted
            // mid-read and every revisit recomputes from scratch. 200k clears
            // the largest page with headroom.
            resultCacheMaxSize: 200_000
        }),
        defaultOptions: {
            query: {
                fetchPolicy: "cache-first"
            }
        }
    });

    return apolloClient;
};

export const ApolloClientProvider: React.FC<PropsWithChildren> = ({
    children
}) => {
    const { getAccessTokenSilently } = useAuth0();

    const apolloClient = createApolloClient(async () => {
        try {
            return await getAccessTokenSilently();
        } catch (error) {
            console.error("Error getting access token:", error);
            return undefined;
        }
    });

    return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
